import React, { useState } from 'react';
import { IoInformationCircleOutline, IoAdd, IoCheckmark, IoChevronForward } from 'react-icons/io5';
import { useFamilyData } from '@/hooks/useFamilyData';
import './MissingInfoPrompts.css';

interface MissingInfoPromptsProps {
  missingInfo: string[];
}

const MissingInfoPrompts: React.FC<MissingInfoPromptsProps> = ({ missingInfo }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState<Set<number>>(new Set());
  const { completeFamilyData } = useFamilyData();

  const handleSubmitResponse = async () => {
    if (!currentResponse?.trim()) return;

    setIsSubmitting(true);
    try {
      await completeFamilyData(currentResponse);
      
      // Mark current question as completed
      setCompletedQuestions(prev => new Set([...prev, currentQuestionIndex]));
      
      // Clear response and move to next question
      setCurrentResponse('');
      
      // Find next unanswered question
      const nextIndex = missingInfo.findIndex((_, index) => 
        index > currentQuestionIndex && !completedQuestions.has(index)
      );
      
      if (nextIndex !== -1) {
        setCurrentQuestionIndex(nextIndex);
      } else {
        // If no more questions, go to first unanswered question
        const firstUnanswered = missingInfo.findIndex((_, index) => 
          !completedQuestions.has(index) && index !== currentQuestionIndex
        );
        if (firstUnanswered !== -1) {
          setCurrentQuestionIndex(firstUnanswered);
        }
      }
    } catch (error) {
      console.error('Failed to submit response:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuestionSelect = (index: number) => {
    if (!completedQuestions.has(index)) {
      setCurrentQuestionIndex(index);
      setCurrentResponse('');
    }
  };

  if (!missingInfo || missingInfo.length === 0) {
    return null;
  }

  const remainingQuestions = missingInfo.filter((_, index) => !completedQuestions.has(index));
  const currentQuestion = missingInfo[currentQuestionIndex];

  return (
    <div className="missing-info-prompts">
      <div className="missing-info-prompts-header">
        <IoInformationCircleOutline className="missing-info-prompts-icon" />
        <div>
          <h4 className="missing-info-prompts-title">Complete Your Family Information</h4>
          <p className="missing-info-prompts-subtitle">
            {remainingQuestions.length} question{remainingQuestions.length !== 1 ? 's' : ''} remaining
          </p>
        </div>
      </div>

      {/* Questions List */}
      <div className="missing-info-questions-list">
        <h5 className="missing-info-questions-title">Questions to Answer:</h5>
        <ul className="missing-info-questions-bullets">
          {missingInfo.map((prompt, index) => (
            <li 
              key={index} 
              className={`missing-info-question-bullet ${
                completedQuestions.has(index) ? 'completed' : 
                index === currentQuestionIndex ? 'active' : 'pending'
              }`}
              onClick={() => handleQuestionSelect(index)}
            >
              <div className="missing-info-question-bullet-icon">
                {completedQuestions.has(index) ? (
                  <IoCheckmark />
                ) : index === currentQuestionIndex ? (
                  <IoChevronForward />
                ) : (
                  <span className="missing-info-question-number">{index + 1}</span>
                )}
              </div>
              <span className="missing-info-question-text">{prompt}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Current Question Input */}
      {currentQuestion && !completedQuestions.has(currentQuestionIndex) && (
        <div className="missing-info-current-question">
          <div className="missing-info-current-question-header">
            <h5 className="missing-info-current-question-title">
              Question {currentQuestionIndex + 1} of {missingInfo.length}
            </h5>
            <p className="missing-info-current-question-text">{currentQuestion}</p>
          </div>
          
          <div className="missing-info-current-response">
            <textarea
              value={currentResponse}
              onChange={(e) => setCurrentResponse(e.target.value)}
              placeholder="Type your answer here..."
              className="missing-info-current-textarea"
              rows={4}
              disabled={isSubmitting}
            />
            
            <div className="missing-info-current-actions">
              <button
                onClick={handleSubmitResponse}
                disabled={!currentResponse?.trim() || isSubmitting}
                className="missing-info-current-submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="missing-info-current-spinner" />
                    Adding Information...
                  </>
                ) : (
                  <>
                    <IoAdd />
                    Add & Continue
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="missing-info-prompts-footer">
        <div className="missing-info-prompts-progress">
          <div className="missing-info-prompts-progress-bar">
            <div 
              className="missing-info-prompts-progress-fill"
              style={{ 
                width: `${(completedQuestions.size / missingInfo.length) * 100}%` 
              }}
            />
          </div>
          <span className="missing-info-prompts-progress-text">
            {completedQuestions.size} of {missingInfo.length} questions completed
          </span>
        </div>
      </div>
    </div>
  );
};

export default MissingInfoPrompts;